import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { CameraListProvider } from "../camera-scanner/camera-list-provider";
import { CameraTorchState } from "../../../common/hooks/use-camera-stream";

export enum CodeScannerMode {
    BarcodeScanner = "BarcodeScanner",
    CameraScanner = "CameraScanner",
}

interface ICodeScannerOptionsContext {
    scanMode: CodeScannerMode;
    isCameraScannerAvailable: boolean;
    cameraDevicesList: MediaDeviceInfo[] | undefined;
    preferredCameraDevice: MediaDeviceInfo | undefined;
    torchPreferred: boolean;
    activeCameraTorchStatus: CameraTorchState;

    onScanModeChange: (mode: CodeScannerMode) => void;
    onCameraAccessFailed: () => void;
    onCameraDevicesListUpdated: (list: MediaDeviceInfo[]) => void;
    onPreferredCameraDeviceChange: (device: MediaDeviceInfo) => void;
    onTorchToggle: () => void;
    onCurrentCameraTorchStatusChange: (torchState: CameraTorchState) => void;
}

const defaultCodeScannerContext: ICodeScannerOptionsContext = {
    scanMode: localStorage.getItem("scan_ship_mode") === CodeScannerMode.CameraScanner.toString() ?
        CodeScannerMode.CameraScanner : CodeScannerMode.BarcodeScanner,
    isCameraScannerAvailable: true,
    cameraDevicesList: (new CameraListProvider()).fromStorage() ?? undefined,
    preferredCameraDevice: localStorage.getItem("scan_ship_camera") ?
        { deviceId: localStorage.getItem("scan_ship_camera") } as MediaDeviceInfo : undefined,
    torchPreferred: true,
    activeCameraTorchStatus: "off",

    onScanModeChange: (_mode: CodeScannerMode) => ({}),
    onCameraAccessFailed: () => ({}),
    onCameraDevicesListUpdated: () => ({}),
    onPreferredCameraDeviceChange: () => ({}),
    onTorchToggle: () => ({}),
    onCurrentCameraTorchStatusChange: () => ({}),
};

const CodeScannerOptionsContext = createContext<ICodeScannerOptionsContext>(defaultCodeScannerContext);

interface ICodeScannerOptionsProviderProps {
    children: ReactNode;
}

export const CodeScannerOptionsProvider = ({ children }: ICodeScannerOptionsProviderProps) => {
    const [scanMode, setScanMode] = useState<CodeScannerMode>(defaultCodeScannerContext.scanMode);
    const [isCameraScannerAvailable, setCameraScannerAvailable] = useState(defaultCodeScannerContext.isCameraScannerAvailable);
    const [cameraDevicesList, setCameraDevicesList] = useState<MediaDeviceInfo[] | undefined>(defaultCodeScannerContext.cameraDevicesList);
    const [preferredCameraDevice, setPreferredCameraDevice] = useState<MediaDeviceInfo | undefined>(defaultCodeScannerContext.preferredCameraDevice);
    const [torchPreferred, setTorchPreferred] = useState<boolean>(false);
    const [activeCameraTorchStatus, setActiveCameraTorchStatus] = useState<CameraTorchState>(defaultCodeScannerContext.activeCameraTorchStatus);

    const handleCameraAccessFailed = () => {
        setCameraScannerAvailable(false);
        setActiveCameraTorchStatus("off");
        setScanMode(CodeScannerMode.BarcodeScanner);
    };

    const handlePreferredCameraDeviceChange = (device: MediaDeviceInfo) => {
        setPreferredCameraDevice(prevState => {
            if (prevState?.deviceId === device.deviceId) {
                return prevState;
            }

            setActiveCameraTorchStatus("unknown");
            return device;
        });
    };

    useEffect(() => {
        localStorage.setItem("scan_ship_mode", scanMode.toString());
    }, [scanMode]);

    useEffect(() => {
        if (cameraDevicesList && cameraDevicesList.length > 0) {
            sessionStorage.setItem("scan_ship_camera_list", JSON.stringify(cameraDevicesList));
        }
    }, [cameraDevicesList]);

    useEffect(() => {
        if (preferredCameraDevice) {
            localStorage.setItem("scan_ship_camera", preferredCameraDevice.deviceId);
        } else {
            localStorage.removeItem("scan_ship_camera");
        }
    }, [preferredCameraDevice]);

    return <CodeScannerOptionsContext.Provider value={{
        scanMode,
        onScanModeChange: (mode: CodeScannerMode) => setScanMode(mode),

        isCameraScannerAvailable,
        onCameraAccessFailed: handleCameraAccessFailed,

        cameraDevicesList,
        onCameraDevicesListUpdated: (list: MediaDeviceInfo[]) => setCameraDevicesList(list),

        preferredCameraDevice,
        torchPreferred,
        onPreferredCameraDeviceChange: handlePreferredCameraDeviceChange,
        onTorchToggle: () => setTorchPreferred(prev => !prev),

        activeCameraTorchStatus,
        onCurrentCameraTorchStatusChange: (status: CameraTorchState) => setActiveCameraTorchStatus(status),

    }}>
        {children}
    </CodeScannerOptionsContext.Provider>;
};

export const useCodeScannerOptions = () => {
    return useContext(CodeScannerOptionsContext);
};
